import React, { type ReactNode } from "react";
import type { ColumnDefinition, DataRow } from "../../schema/chart";
import { detectColumns } from "./columnHelpers";
import { DataTable } from "./DataTable";

function extractText(node: ReactNode): string {
    if (node == null || typeof node === "boolean") return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(extractText).join("");
    if (React.isValidElement(node)) {
        const props = node.props as { children?: ReactNode };
        return extractText(props.children);
    }
    return "";
}

function findChildrenByType(children: ReactNode, type: string): React.ReactElement[] {
    const results: React.ReactElement[] = [];
    React.Children.forEach(children, (child) => {
        if (React.isValidElement(child) && child.type === type) {
            results.push(child);
        }
    });
    return results;
}

function parseMarkdownTable(children: ReactNode): {
    headers: string[];
    rows: DataRow[];
} | null {
    const theadElements = findChildrenByType(children, "thead");
    const tbodyElements = findChildrenByType(children, "tbody");

    if (theadElements.length === 0 || tbodyElements.length === 0) return null;

    const theadProps = theadElements[0].props as { children?: ReactNode };
    const headerRows = findChildrenByType(theadProps.children, "tr");
    if (headerRows.length === 0) return null;

    const headerRowProps = headerRows[0].props as { children?: ReactNode };
    const thCells = findChildrenByType(headerRowProps.children, "th");
    const headers = thCells.map((th) =>
        extractText((th.props as { children?: ReactNode }).children).trim(),
    );

    if (headers.length === 0) return null;

    const tbodyProps = tbodyElements[0].props as { children?: ReactNode };
    const bodyRows = findChildrenByType(tbodyProps.children, "tr");

    const rows: DataRow[] = bodyRows.map((tr) => {
        const trProps = tr.props as { children?: ReactNode };
        const tdCells = findChildrenByType(trProps.children, "td");
        const row: DataRow = {};
        headers.forEach((header, i) => {
            row[header] =
                i < tdCells.length
                    ? extractText((tdCells[i].props as { children?: ReactNode }).children).trim()
                    : "";
        });
        return row;
    });

    return { headers, rows };
}

export const MarkdownTable: React.FC<{ children?: ReactNode }> = ({ children }) => {
    const parsed = parseMarkdownTable(children);

    if (!parsed || parsed.headers.length === 0) {
        return <table>{children}</table>;
    }

    const columns: ColumnDefinition[] = detectColumns(parsed.headers, parsed.rows);

    return (
        <DataTable
            columns={columns}
            rows={parsed.rows}
            onChange={() => undefined}
            readOnly
            exportFilename="table"
        />
    );
};
